
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { generateEmailResponse } from "@/lib/openai";
import { convertMarkdownToHtml } from "@/utils/markdownUtils";
import { useTokenValidation } from "./email/useTokenValidation";
import { useOpenAIKey } from "./email/useOpenAIKey";
import { useEmailSender } from "./email/useEmailSender";
import { extractEmail } from "@/utils/email/emailUtils";

interface Email {
  id: string;
  subject: string;
  sender: string;
  preview: string;
  date: string;
  content?: string;
  htmlContent?: string;
}

interface SendEmailOptions {
  subject: string;
  cc?: string;
  bcc?: string;
  attachments?: File[];
}

export const useResponseGeneration = (email: Email, selectedAssistant: string) => {
  const [response, setResponse] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const { validateTokenBalance } = useTokenValidation();
  const { sendEmail, isSending } = useEmailSender();

  const getTokenCost = async (modelName: string = 'gpt-4o-mini') => {
    console.log("[Token Cost] Fetching token cost for model:", modelName);
    const { data: costData, error: costError } = await supabase
      .from('token_model_costs')
      .select('id, token_cost')
      .eq('model_name', modelName)
      .single();

    if (costError) {
      console.error("[Token Cost] Error fetching token cost:", costError);
      return { cost: 1, modelId: null };
    }

    console.log("[Token Cost] Retrieved data:", costData);
    return { cost: costData?.token_cost || 1, modelId: costData?.id };
  };

  const deductTokens = async (tokenCost: number, modelId: string | null) => {
    console.log("[Token Deduction] Starting token deduction:", {
      cost: tokenCost,
      modelId: modelId
    });
    
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      console.error("[Token Deduction] No user session found");
      throw new Error('User not authenticated');
    }

    const { error: updateError } = await supabase.rpc('add_tokens', {
      p_user_id: session.user.id,
      p_amount: -tokenCost
    });

    if (updateError) {
      console.error("[Token Deduction] Error updating token balance:", updateError);
      throw new Error('Failed to update token balance');
    }

    console.log(`[Token Deduction] Successfully deducted ${tokenCost} tokens`);

    const { error: transactionError } = await supabase
      .from('token_transactions')
      .insert({
        user_id: session.user.id,
        amount: -tokenCost,
        transaction_type: 'usage',
        description: 'Email response generation',
        model_id: modelId
      });

    if (transactionError) {
      console.error("[Token Deduction] Error recording transaction:", transactionError);
    }
  };

  const generateResponse = async (userComment?: string) => {
    console.log("[Response Generation] Starting for email:", {
      emailId: email.id,
      subject: email.subject,
      selectedAssistantId: selectedAssistant,
      hasUserComment: !!userComment
    });
    
    setGenerationError(null);
    
    if (!selectedAssistant) {
      console.error("[Response Generation] No assistant selected");
      toast.error("Please select an AI assistant first.");
      return;
    }

    setIsGenerating(true);
    
    try {
      const { cost, modelId } = await getTokenCost('gpt-4o-mini');
      console.log("[Response Generation] Token cost calculated:", { cost, modelId });
      
      const isValid = await validateTokenBalance(cost);
      if (!isValid) {
        console.log("[Response Generation] Token validation failed");
        setIsGenerating(false);
        return;
      }

      console.log("[Response Generation] Fetching assistant details for ID:", selectedAssistant);
      const { data: assistantData, error: assistantError } = await supabase
        .from('assistants')
        .select('assistant_id, name, model')
        .eq('id', selectedAssistant)
        .single();

      if (assistantError || !assistantData?.assistant_id) {
        console.error("[Response Generation] Error fetching assistant:", assistantError);
        toast.error("Failed to get assistant details. Please try again.");
        setIsGenerating(false);
        return;
      }

      console.log("[Response Generation] Using assistant:", {
        databaseId: selectedAssistant,
        openAiId: assistantData.assistant_id,
        name: assistantData.name,
        model: assistantData.model
      });
      
      try {
        const generatedResponse = await generateEmailResponse(
          email, 
          assistantData.assistant_id,
          userComment
        );
        console.log("[Response Generation] Response received from OpenAI");
        
        const htmlResponse = await convertMarkdownToHtml(generatedResponse);
        setResponse(htmlResponse);
        setHasGenerated(true);

        await deductTokens(cost, modelId);
        
        toast.success("Response generated successfully");
      } catch (error: any) {
        console.error("[Response Generation] Error from OpenAI:", error);
        
        // Provide more specific error messages based on error type
        let errorMessage = error?.message || "Unknown error occurred";
        
        if (errorMessage.includes("rate limit") || errorMessage.includes("quota exceeded")) {
          errorMessage = "OpenAI API quota exceeded. Please check your OpenAI account billing status.";
          toast.error("OpenAI Error: API quota exceeded. Your OpenAI account needs payment or a quota increase.");
        } else if (errorMessage.includes("authentication")) {
          errorMessage = "Authentication error with OpenAI. Please check your API key.";
          toast.error("OpenAI Error: Authentication failed. Please check your API key configuration.");
        } else {
          toast.error(`OpenAI Error: ${errorMessage}`);
        }
        
        setGenerationError(errorMessage);
      }
    } catch (error: any) {
      console.error("[Response Generation] Error:", error);
      setGenerationError(error?.message || "Unknown error");
      toast.error(error?.message || "Failed to generate response. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendResponse = async (options: SendEmailOptions) => {
    console.log("[Email Sender] Sending email with options:", {
      subject: options.subject,
      hasCc: !!options.cc,
      hasBcc: !!options.bcc,
      attachmentsCount: options.attachments?.length || 0
    });
    
    try {
      // Get the connected email accounts to use for sending
      const { data: accounts, error: accountsError } = await supabase
        .from('email_accounts')
        .select('*')
        .limit(1);
      
      if (accountsError || !accounts?.length) {
        console.error("Error fetching connected email accounts:", accountsError);
        toast.error("No connected email accounts found. Please connect an email account in Settings.");
        return false;
      }

      // Use the connected account to send the email
      const connectedAccount = accounts[0];
      console.log("Using connected account for sending:", connectedAccount.email);
      
      // Get the recipient's actual email address from the sender field
      const recipientEmail = extractEmail(email.sender);
      console.log("Sending response to:", recipientEmail);
      
      const result = await sendEmail(
        connectedAccount.email, // Use the connected account email as sender
        recipientEmail,         // Send to the original sender's email
        response, 
        options
      );
      
      if (result) {
        setEmailSent(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error sending email:", error);
      return false;
    }
  };

  return {
    response,
    setResponse,
    isGenerating,
    hasGenerated,
    emailSent,
    generationError,
    generateResponse,
    handleSendResponse
  };
};
